import React, { useState, useCallback } from 'react'
import { useMachine } from '@xstate/react'

import './styles.css'
import questions from './questions'
import assessmentMachine from './assessmentMachine'

// Enable next line for production:
const devTools = false

// Enable next 7 lines for development
// import { inspect } from "@xstate/inspect";
// const devTools = true;
// inspect({
//   // options
//   // url: 'https://statecharts.io/inspect', // (default)
//   iframe: false // open in new window
// });

let startTime = new Date()

const SUMMARY_SCREEN_ILLUSTRATION = 'https://meru-program.b-cdn.net/results_lady.png'

window.addEventListener('keydown', (e) => {
  if (!devTools && !window.devTools) {
    return
  }

  // Util to quickly click through the screener if devTools is true or you have types "devTools=true" to console
  if (!isNaN(e.key)) {
    let input = document.querySelector(`.current .option-row:nth-child(${e.key}) input`)
    input && input.click()
  } else if (e.key === 'Enter') {
    let button = document.querySelector('.current button')
    button && button.click()
  }
})

function getSearchVariable(variable, defaultValue = '') {
  let query = window.location.search.substring(1);
  let vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    let pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
  return defaultValue
}

const Option = ({ label, name, value, onClick, id, type = 'radio' }) => (
  <div className="option-row">
    <input type={type} className="radio" name={name} value={value} onClick={onClick} id={id} />{' '}
    <label className={"radio-label radio-label-type-" + type} htmlFor={id}>
      {label}
    </label>
  </div>
)

// Also used for GAD7 questions
const PHQ9Question = ({ questionID, question, className, onAnswer }) => {
  const [score, setScore] = useState(undefined)
  const option = (score, label) => (
    <Option
      label={label}
      name={questionID}
      onClick={() => setScore(score)}
      id={`${questionID}_${score}`}
    />
  )
  return (
    <div className={`${className} question`}>
      <h3>Over the last 2 weeks, how often have you been bothered by the following problem?</h3>
      <h2>{question}</h2>
      <div className="options">
        {option(0, "Not at all")}
        {option(1, "Several days")}
        {option(2, "More than half of the days")}
        {option(3, "Nearly every day")}
      </div>
      <Button disabled={score === undefined} onClick={() => onAnswer(score)} />
    </div>
  )
}

const YesNo = ({ questionID, question, className, onAnswer }) => {
  const [score, setScore] = useState(undefined)
  return (
    <div className={`${className} question`}>
      <h2>{question}</h2>
      <div className="options mhqoptions">
        <Option
          label="No"
          name={questionID}
          onClick={() => setScore(0)}
          id={`${questionID}_no`}
        />
        <Option
          label="Yes"
          name={questionID}
          onClick={() => setScore(1)}
          id={`${questionID}_yes`}
        />
      </div>
      <Button disabled={score === undefined} onClick={() => onAnswer(score)} />
    </div>
  )
}

const Button = ({ disabled, onClick, text = "Next" }) => {
  const [wasPressed, setPressed] = useState(false)
  return (
    <div className="sqs-block button-block sqs-block-button">
      <div className="sqs-block-button-container--center">
        <button
          className="sqs-block-button-element--medium sqs-block-button-element"
          disabled={Boolean(disabled || wasPressed)}
          onClick={() => {
            setPressed(true)
            if (onClick) {
              onClick()
            }
          }}
        >
          {text}
        </button>
      </div>
    </div>
  )
}

const LinkButton = ({ children, ...props }) => (
  <div className="sqs-block button-block sqs-block-button">
    <div className="sqs-block-button-container--center">
      <a {...props} className="sqs-block-button-element--medium sqs-block-button-element">
        {children}
      </a>
    </div>
  </div>
)

const getTherapyLink = () =>
  `https://total.meruhealth.com/${getSearchVariable('t', 'therapyprogram')}?s=${getSearchVariable('s')}`

function AdditionalSupport({ onAnswer }) {
  return (
    <div className="additional-support selectable">
      <h3>We recommend seeking additional support</h3>
      <p>
        Thank you for your honest responses. It’s common to sometimes feel overwhelmed, hopeless, or even question if
        life is worth living. These thoughts don’t signify weakness, but often do signal the need for additional
        support.
      </p>
      <p>
        Please remember that you are not alone. If you’re having thoughts or strong urges to hurt yourself, get help
        right away and go to your local Emergency room.
      </p>
      <p>
        Another way to get immediate support is with caring and trained professionals who are just a phone call or text
        away, and available 24/7.
      </p>
      <br />
      <br />
      <span className="green">National Suicide Prevention Hotline Number</span>: 1-800-273-8255
      <br />
      <span className="green">Crisis Text Line</span>: Text the word <span className="green">HOME</span> to 741741
      <br />
      <br />
      <p>
        Trust that while these thoughts and feelings are not uncommon, it is possible to feel better and you are doing
        courageous work here to tackle them!
      </p>
      <Button onClick={onAnswer} />
    </div>
  )
}

const Therapy = () => (
  <div>
    <div className="selectable">
      <h2>It seems like you're experiencing some anxiety or depression.</h2>
      <p>We've prepared a path tailored to your current needs that will help you achieve long-term results.</p>
    </div>
    <LinkButton href={getTherapyLink()}>Show my recommendation</LinkButton>
    <img src={SUMMARY_SCREEN_ILLUSTRATION} alt="Lady holding a tablet" className="summary-screen-illustration" />
  </div>
)

const Coaching = () => (
  <div>
    <div className="selectable">
      <h2>Your recommendation is ready!</h2>
      <p>
        Based on your responses, we've prepared a path that is proven to reduce stress, increase well being and achieve
        long-term results!
      </p>
    </div>
    <LinkButton href={`https://total.meruhealth.com/meru-app-coaching?s=${getSearchVariable('s', 'null')}`}>
      Show my recommendation
    </LinkButton>
    <img src={SUMMARY_SCREEN_ILLUSTRATION} alt="Lady holding a tablet" className="summary-screen-illustration" />
  </div>
)

const getLeaf = (obj) => {
  if (typeof obj === 'string' || !obj) return obj
  return getLeaf(Object.values(obj).find((d) => d))
}

const trackInAnalytics = (ctx, event, actionMeta) => {
  const eventName = `coaching_assessment:${actionMeta.action.eventName}`
  if (!window.mixpanel) {
    console.warn(`Mixpanel not initialized. Tracking`, eventName)
    return
  }
  window.mixpanel.track(eventName, {
      source: getSearchVariable('s')
  })
}

function saveSmartScreenerResultsResults(context, event, actionMeta) {
  let data = {}
  context.answers.forEach(a => data[a.question] = a.answer)

    data.source = getSearchVariable('s')
    data.suggestion = actionMeta.action.suggestion
    data.duration = Math.round((new Date() - startTime) / 1000)
    data.isSuicidal = context.isSuicidal

    if (window.localStorage && localStorage.meruScreenerID) {
        data.screenerID = localStorage.meruScreenerID
    }

  let dataString = Object.keys(data).map(k => data[k] !== undefined ? `${k}=${data[k]}` : null).filter(Boolean).join('&')
  let xhr = new XMLHttpRequest();
  xhr.open('GET', `https://us-central1-meru-ok.cloudfunctions.net/saveSmartScreenerResults?${dataString}`);
  xhr.send()
}

const machineOptions = {
  devTools,
  actions: {
    trackInAnalytics,
    saveSmartScreenerResultsResults
  },
}

export default function App() {
  const [state, send] = useMachine(assessmentMachine, machineOptions)
  const matches = useCallback((id) => getLeaf(state.value) === id, [state])
  const getClassName = id => matches(id) ? 'current' : ''

  const renderQuestion = function({id, question, type, inverted = false}) {
    if (type === 'PHQ9') {
        return (
            <PHQ9Question
                key={id}
                questionID={id}
                className={getClassName(id)}
                question={question}
                onAnswer={(answer) =>
                    send({
                        type: id.includes('phq9') ? 'PHQ9' : 'GAD7',
                        question: id,
                        answer,
                    })
                }
            />
        )
    } else if (type === 'YesNo') {
        return (
            <YesNo
                key={id}
                questionID={id}
                className={getClassName(id)}
                question={question}
                onAnswer={(answer) =>
                    send({
                        type: answer ? 'YES' : 'NO',
                        question: id,
                    })
                }
            />
        )
    }
    return null
  }

  return (
    <div className="App">
      {
        state.matches('therapy') ? (
          <Therapy context={state.context} />
        ) : state.matches('coaching') ? (
          <Coaching context={state.context} />
        ) : matches('additionalSupport') ? (
          <AdditionalSupport onAnswer={() => send({type: 'NEXT'})} />
        ) : (
          questions.map(renderQuestion)
        )
      }
    </div>
  )
}
